import React, { useState, useContext, useEffect } from "react";
import { Button, Modal, Form } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import StarekTreeView from "../StarekTreeView";
import "../../starek.css";
import PlantShiftWizard from "./PlantShiftWizard";
import StarekContext from "../../context/StarekContext";
import { gql, useMutation } from "@apollo/client";

function AddPlantModal({
  show,
  handleClose,
  onSuccess,
  plantData = null,
  mode = "add",
}) {
  const [shiftOverlapError, setShiftOverlapError] = useState(false);
  const [overlappingShifts, setOverlappingShifts] = useState([]);

  const [currentScreen, setCurrentScreen] = useState(1);
  const [visitedScreens, setVisitedScreens] = useState([1]);
  const [zoneData, setZoneData] = useState([]);
  const [shiftData, setShiftData] = useState([]);
  const [defaultShift, setDefaultShift] = useState({
    id: "default",
    name: "Default shift",
    start: 28800, // 8am in seconds
    end: 61200, // 5pm in seconds
    runs_on: 62, // Monday to Friday by default
  });
  const [plantImage, setPlantImage] = useState("");
  const MUTATION_ONBOARD_PLANT = gql`
    mutation CreatePlantMutation($data: PlantOnboardInput!) {
      plantOnboard(data: $data) {
        name
        code
        description
        created_by_id
      }
    }
  `;

  const MUTATION_UPDATE_PLANT = gql`
    mutation UpdateOnePlant(
      $data: PlantUpdateInput!
      $where: PlantWhereUniqueInput!
    ) {
      updateOnePlant(data: $data, where: $where) {
        id
        name
        description
      }
    }
  `;

  const [plantName, setPlantName] = useState("");
  const [plantDescription, setPlantDescription] = useState("");
  const [plantNameError, setPlantNameError] = useState("");
  const [adminName, setAdminName] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const { auth } = useContext(StarekContext);
  const [onboardPlant, { data, loading, error }] = useMutation(
    MUTATION_ONBOARD_PLANT
  );
  const [updatePlant, { loading: updateLoading }] = useMutation(
    MUTATION_UPDATE_PLANT
  );

  const screens = [
    { id: 1, icon: "fa-industry", label: "Screen 1" },
    { id: 2, icon: "fa-bullseye", label: "Screen 2" },
    { id: 3, icon: "fa-clipboard", label: "Screen 3" },
    { id: 4, icon: "fa-flag", label: "Screen 4" },
  ];

  //thinking of adding this in a utility file
  const normalize = (s, e) => [s, e <= s ? e + 86400 : e];

  const overlaps = (aStart, aEnd, bStart, bEnd) => {
    let [A1, A2] = normalize(aStart, aEnd);
    let [B1, B2] = normalize(bStart, bEnd);
    const o1 = A1 < B2 && B1 < A2;
    const o2 = A1 + 86400 < B2 + 86400 && B1 + 86400 < A2 + 86400;
    const o3 = A1 < B2 + 86400 && B1 + 86400 < A2;
    return o1 || o2 || o3;
  };

  const validateAllShifts = () => {
    const shifts = getAllShifts();
    const overlapsList = [];

    for (let i = 0; i < shifts.length; i++) {
      for (let j = i + 1; j < shifts.length; j++) {
        const s1 = shifts[i];
        const s2 = shifts[j];

        if (overlaps(s1.start, s1.end, s2.start, s2.end)) {
          overlapsList.push({
            shift1: s1.name || "Default shift",
            shift2: s2.name || "New shift",
            shift1Start: s1.start,
            shift1End: s1.end,
            shift2Start: s2.start,
            shift2End: s2.end,
          });
        }
      }
    }

    if (overlapsList.length) {
      setShiftOverlapError(overlapsList);
      return false;
    }
    setShiftOverlapError([]);
    return true;
  };

  useEffect(() => {
    if (mode === "edit" && plantData) {
      setPlantName(plantData.name || "");
      setPlantDescription(plantData.description || "");

      const buildZoneTree = (zones) => {
        const map = {};
        const roots = [];

        zones.forEach((zone) => {
          map[zone.id] = {
            id: zone.id.toString(),
            label: zone.name || "Unnamed Zone",
            description: zone.description,
            children: [],
          };
        });

        zones.forEach((zone) => {
          if (zone.parent_id) {
            const parent = map[zone.parent_id];
            if (parent) {
              parent.children.push(map[zone.id]);
            }
          } else {
            roots.push(map[zone.id]);
          }
        });

        return roots;
      };

      const nestedZones = buildZoneTree(plantData.zones || []);
      setZoneData(nestedZones);

      const structuredShifts = (plantData.shifts || []).map((shift) => ({
        id: shift.id.toString(),
        name: shift.name,
        start: shift.start_time ?? 0,
        end: shift.end_time ?? 28800,
        runs_on: shift.runs_on ?? 62,
      }));
      setShiftData(structuredShifts);
    }
  }, [plantData, mode]);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}`;
  };

  const handleNext = () => {
    if (currentScreen === 1) {
      if (!plantName.trim()) {
        setPlantNameError("Plant Name is required");
        return;
      } else {
        setPlantNameError("");
      }
    }

    if (currentScreen === 3) {
      if (!validateAllShifts()) {
        return;
      }
    }

    const nextScreen = currentScreen + 1;
    if (nextScreen <= screens.length) {
      setCurrentScreen(nextScreen);
      setVisitedScreens((prev) => [...new Set([...prev, nextScreen])]);
    }
  };
  const getIconBackground = (screenId) => {
    if (screenId === currentScreen) {
      return "bg-color-007bff";
    }
    if (visitedScreens.includes(screenId)) {
      return "bg-color-20ac6b";
    }
    return "bg-color-f2f2f2";
  };
  const getIconColor = (screenId) => {
    if (screenId === currentScreen || visitedScreens.includes(screenId)) {
      return "text-light";
    }
    return "font-color-a9b0bc";
  };
  const getHrBackground = (screenId) => {
    if (
      visitedScreens.includes(screenId) &&
      visitedScreens.includes(screenId + 1)
    ) {
      return "bg-color-20ac6b";
    } else if (visitedScreens.includes(screenId)) {
      return "bg-hr-gradient";
    }
    return "bg-color-f2f2f2";
  };
  const handleAddShift = () => {
    const newShift = {
      id: `shift_${Date.now()}`,
      name: `New shift`,
      start: 0, // 0am in seconds
      end: 28800, // 8am in seconds
      runs_on: 62, // Monday to Friday by default
    };

    setShiftData([...shiftData, newShift]);
  };
  const handleRemoveShift = (id) => {
    const updatedShifts = shiftData.filter((shift) => shift.id !== id);
    setShiftData(updatedShifts);
  };

  const handleBackBtnClick = () => {
    const prevScreen = currentScreen - 1;
    if (prevScreen >= 1) {
      setCurrentScreen(prevScreen);
      setVisitedScreens((prev) =>
        prev.filter((screen) => screen !== currentScreen)
      );
    }
  };

  const countTotalZones = (zones) => {
    let count = zones.length;
    zones.forEach((zone) => {
      if (zone.children && zone.children.length > 0) {
        count += countTotalZones(zone.children);
      }
    });
    return count;
  };

  const handleDefaultShiftChange = (updatedShift) => {
    setDefaultShift({
      ...defaultShift,
      ...updatedShift,
    });
  };

  const handleShiftChange = (updatedShift) => {
    const updatedShifts = shiftData.map((shift) =>
      shift.id === updatedShift.id ? { ...shift, ...updatedShift } : shift
    );
    setShiftData(updatedShifts);
  };

  const buildPlantPayload = (
    mode,
    { plantName, plantDescription, shiftData, zoneData }
  ) => {
    const isCreateMode = mode === "add";
    const userId = 1;
    const plantId = plantData?.id;

    const prepareShiftsForCreate = (shiftData || []).map((s) => ({
      name: s.name || "Unnamed Shift",
      description: s.description || "New Shift",
      start_time: s.start ?? s.start_time ?? 28800,
      end_time: s.end ?? s.end_time ?? 61200,
      runs_on: s.runs_on ?? 62,
      created_by_id: userId,
    }));

    const prepareZonesForCreate = (zones) =>
      (zones || []).map((zone) => ({
        name: zone.label || zone.name || "Unnamed Zone",
        description: zone.description || "New Zone",
        created_by_id: userId,
        ...(Array.isArray(zone.children) && zone.children.length > 0
          ? { zones: prepareZonesForCreate(zone.children) }
          : {}),
      }));

    const prepareShiftsForEdit = (shiftData || []).map((s) => ({
      name: s.name || "Unnamed Shift",
      description: s.description || "New Shift",
      start_time: s.start ?? s.start_time ?? 28800,
      end_time: s.end ?? s.end_time ?? 61200,
      runs_on: s.runs_on ?? 62,
      created_by: { connect: { id: userId } },
      updated_by: { connect: { id: userId } },
    }));

    const prepareZonesForEdit = (zones, depth = 0) =>
      (zones || []).map((zone) => ({
        name: zone.label || zone.name || "Unnamed Zone",
        description: zone.description || "New Zone",
        created_by: { connect: { id: userId } },
        updated_by: { connect: { id: userId } },
        plant: depth > 0 ? { connect: { id: plantId } } : undefined,
        ...(Array.isArray(zone.children) && zone.children.length > 0
          ? { zones: { create: prepareZonesForEdit(zone.children, depth + 1) } }
          : {}),
      }));

    if (isCreateMode) {
      return {
        name: plantName || "Unnamed Plant",
        description: plantDescription || "New Plant",
        created_by_id: userId,
        shifts: prepareShiftsForCreate,
        zones: prepareZonesForCreate(zoneData),
      };
    } else {
      return {
        name: { set: plantName || "Unnamed Plant" },
        description: { set: plantDescription || "New Plant" },
        updated_by: { connect: { id: userId } },
        shifts: {
          deleteMany: {},
          create: prepareShiftsForEdit,
        },
        zones: {
          deleteMany: {},
          create: prepareZonesForEdit(zoneData),
        },
      };
    }
  };

  const handleSubmit = async () => {
    const isUntouchedPlaceholder = (shift) => {
      const isDefaultId = shift.shiftId === "default";
      const nameIsDefault = !shift.name || shift.name === "Default shift";
      const startIsDefault =
        (shift.start ?? shift.start_time) === 28800 ||
        (shift.start == null && shift.start_time == null);
      const endIsDefault =
        (shift.end ?? shift.end_time) === 61200 ||
        (shift.end == null && shift.end_time == null);
      const runsOnDefault =
        (shift.runs_on ?? 62) === 62 || shift.runs_on == null;

      return (
        isDefaultId &&
        nameIsDefault &&
        startIsDefault &&
        endIsDefault &&
        runsOnDefault
      );
    };

    const mapShiftToPayload = (s) => ({
      name: s.name || (s.shiftId === "default" ? "Default shift" : "New shift"),
      description:
        s.description ||
        (s.shiftId === "default"
          ? "Default working hours"
          : "Additional shift"),
      start_time: s.start ?? s.start_time ?? 28800,
      end_time: s.end ?? s.end_time ?? 61200,
      runs_on: s.runs_on ?? 62,
      created_by_id: 1,
    });

    let preparedShifts = (shiftData || [])
      .filter((s) => !isUntouchedPlaceholder(s))
      .map(mapShiftToPayload);

    if (preparedShifts.length === 0) {
      preparedShifts.push({
        name: "Default shift",
        description: "Default working hours",
        start_time: 28800,
        end_time: 61200,
        runs_on: 62,
        created_by_id: 1,
      });
    }

    const plantInputData = buildPlantPayload(mode, {
      plantName,
      plantDescription,
      shiftData: getAllShifts().filter((s) => !isUntouchedPlaceholder(s)),
      zoneData,
    });

    try {
      if (mode === "edit" && plantData?.id) {
        await updatePlant({
          variables: {
            data: plantInputData,
            where: { id: plantData.id },
          },
        });
      } else {
        await onboardPlant({
          variables: { data: plantInputData },
        });
      }

      onSuccess && onSuccess();
      handleClose();
    } catch (err) {
      console.error("Error during plant save:", err);
    }
  };

  const getAllShifts = () => {
    return mode === "add" ? [defaultShift, ...shiftData] : shiftData;
  };

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        backdrop="static"
        size="xl"
        centered
      >
        <Modal.Header className="d-flex align-items-center justify-content-between">
          <Modal.Title>
            {mode === "edit" ? "Edit Plant Wizard" : "Add Plant Wizard"}
          </Modal.Title>
          <div className="d-flex align-items-center">
            {screens.map((screen, index) => (
              <React.Fragment key={screen.id}>
                <span
                  className={`mx-1 border rounded-circle ${getIconBackground(
                    screen.id
                  )}`}
                  style={{
                    padding:
                      screen.id === 3 ? "5px 10px 5px 10px" : "5px 8px 5px 8px",
                  }}
                >
                  <i
                    className={`fa ${screen.icon} ${getIconColor(screen.id)}`}
                  ></i>
                </span>
                {index < screens.length - 1 && (
                  <hr
                    className={`w-30px hr-progress ${getHrBackground(
                      screen.id
                    )}`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </Modal.Header>
        <Modal.Body>
          {currentScreen === 1 && (
            <div className="row">
              <div className="col-8">
                <div className="row">
                  <div className="font-weight-bold mb-3">Plant Details</div>
                  <Form className="col-12 w-100 font-weight-normal max-height-350px overflow-auto">
                    <div className="row">
                      <Form.Group className="col-6 mb-3" controlId="plant_name">
                        <Form.Label>
                          Plant Name <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter here"
                          value={plantName}
                          onChange={(e) => {
                            setPlantName(e.target.value);
                            if (e.target.value.trim()) {
                              setPlantNameError("");
                            }
                          }}
                          isInvalid={!!plantNameError}
                        />
                        <Form.Control.Feedback type="invalid">
                          {plantNameError}
                        </Form.Control.Feedback>
                      </Form.Group>
                      <Form.Group
                        className="col-6 mb-3"
                        controlId="plant_image"
                      >
                        <Form.Label>Plant Image</Form.Label>
                        <Form.Select
                          aria-label="Default select example"
                          value={plantImage}
                          onChange={(e) => setPlantImage(e.target.value)}
                        >
                          <option>Open this select menu</option>
                          <option value="1">One</option>
                          <option value="2">Two</option>
                          <option value="3">Three</option>
                        </Form.Select>
                      </Form.Group>
                      <Form.Group
                        className="col-12 mb-3"
                        controlId="plant_description"
                      >
                        <Form.Label>Plant Description</Form.Label>
                        <Form.Control
                          as="textarea"
                          placeholder="Max allowed characters: 120"
                          rows={3}
                          value={plantDescription}
                          onChange={(e) => setPlantDescription(e.target.value)}
                        />
                      </Form.Group>
                      <Form.Group className="col-6 mb-3" controlId="admin_name">
                        <Form.Label>
                          Administrative Name{" "}
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter here"
                          value={adminName}
                          onChange={(e) => setAdminName(e.target.value)}
                        />
                      </Form.Group>

                      <Form.Group
                        className="col-6 mb-3"
                        controlId="admin_password"
                      >
                        <Form.Label>
                          Administrative Password{" "}
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <div className="input-group">
                          <Form.Control
                            type={showPassword ? "text" : "password"}
                            placeholder="Enter here"
                            value={adminPassword}
                            onChange={(e) => setAdminPassword(e.target.value)}
                          />
                          <span
                            className="input-group-text"
                            style={{ cursor: "pointer" }}
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            <i
                              className={`fa ${
                                showPassword ? "fa-eye-slash" : "fa-eye"
                              }`}
                            ></i>
                          </span>
                        </div>
                      </Form.Group>
                    </div>
                  </Form>
                </div>
              </div>
              <div className="col-4">
                <div className="bg-color-fcfcfc border h-100 p-3 rounded">
                  <div className="font-weight-bold">Plant:</div>
                  <div className="mb-3 mt-1">
                    Identifies your manfacturing unit. Your devices, data and
                    reports belong to a plant.
                  </div>
                  <div className="font-weight-bold">Plant Name:</div>
                  <div className="mb-3 mt-1">
                    Choose a name that helps you identify a plant, factory or a
                    warehouse.
                  </div>
                </div>
              </div>
            </div>
          )}
          {currentScreen === 2 && (
            <div className="row">
              <div className="col-8">
                <div className="row">
                  <div className="d-flex justify-content-start font-weight-bold mb-3">
                    <div>Define Zones</div>
                  </div>
                  <div className="col-12 max-height-350px overflow-auto">
                    <StarekTreeView
                      treeData={zoneData}
                      setTreeData={setZoneData}
                    ></StarekTreeView>
                  </div>
                </div>
              </div>
              <div className="col-4">
                <div className="bg-color-fcfcfc border h-100 p-3 rounded">
                  <div className="font-weight-bold">Zone:</div>
                  <div className="mb-3 mt-1">
                    Identifies your manfacturing unit. Your devices, data and
                    reports belong to a plant.
                  </div>
                  <div className="font-weight-bold">Instance:</div>
                  <div className="mb-3 mt-1">
                    Choose a name that helps you identify a plant, factory or a
                    warehouse.
                  </div>
                  <div className="font-weight-bold">Note:</div>
                  <div className="mb-3 mt-1">
                    The administrative can carry out all possible tasks for a
                    plant, including more users.
                  </div>
                </div>
              </div>
            </div>
          )}
          {currentScreen === 3 && (
            <div className="row">
              <div className="col-8">
                <div className="row">
                  <div className="d-flex justify-content-between font-weight-bold mb-3">
                    <div>Define Shifts</div>
                    <span
                      className="text-primary"
                      style={{ cursor: "pointer" }}
                      onClick={handleAddShift}
                    >
                      Add a Shift
                    </span>
                  </div>
                  {currentScreen === 3 && shiftOverlapError && (
                    <div className="alert alert-danger mb-3">
                      <h6 className="alert-heading">
                        ⚠️ Shift Time Conflicts Detected
                      </h6>
                      <p className="mb-0">
                        Please resolve the following overlapping shifts before
                        proceeding:
                      </p>
                      <ul className="mb-0 mt-2">
                        {overlappingShifts.map((overlap, index) => (
                          <li key={index}>
                            "{overlap.shift1}" ({overlap.time1}) overlaps with "
                            {overlap.shift2}" ({overlap.time2})
                          </li>
                        ))}
                      </ul>
                      <hr />
                      <p className="mb-0 small">
                        To resolve: Click the "Edit Time" button on any
                        conflicting shift and adjust its timing to ensure no
                        overlaps.
                      </p>
                    </div>
                  )}
                  <div className="col-12 max-height-350px overflow-auto">
                    {mode === "add" && (
                      <PlantShiftWizard
                        index={-1}
                        shiftId="default"
                        allShifts={getAllShifts()}
                        runs_on={defaultShift.runs_on}
                        onShiftChange={handleDefaultShiftChange}
                      />
                    )}
                    {shiftData.map((shift, index) => (
                      <PlantShiftWizard
                        key={shift.id}
                        index={index}
                        shiftId={shift.id}
                        shift={shift}
                        allShifts={getAllShifts()}
                        runs_on={shift.runs_on}
                        onShiftChange={handleShiftChange}
                        onDelete={() => handleRemoveShift(shift.id)}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <div className="col-4">
                <div className="bg-color-fcfcfc border h-100 p-3 rounded">
                  <div className="font-weight-bold">Shift:</div>
                  <div className="mb-3 mt-1">
                    Identifies your manfacturing unit. Your devices, data and
                    reports belong to a plant.
                  </div>
                  <div className="font-weight-bold">Instance:</div>
                  <div className="mb-3 mt-1">
                    Choose a name that helps you identify a plant, factory or a
                    warehouse.
                  </div>
                  <div className="font-weight-bold">Note:</div>
                  <div className="mb-3 mt-1">
                    The administrative can carry out all possible tasks for a
                    plant, including more users.
                  </div>
                </div>
              </div>
            </div>
          )}
          {currentScreen === 4 && (
            <div className="row">
              <div className="col-8">
                <div className="row">
                  <div className="font-weight-bold">Final Summary</div>
                  <div className="mb-3">
                    A new plant will be created with the following details:
                  </div>
                  <div className="mb-2">
                    <div className="border p-2 rounded">
                      <div>Plant Name</div>
                      <div className="font-weight-bold">
                        {plantName || "Unnamed Plant"}
                      </div>
                    </div>
                  </div>
                  <div className="mb-2">
                    <div className="border p-2 rounded">
                      <div>Total Zones</div>
                      <div className="font-weight-bold">
                        {countTotalZones(zoneData)}
                      </div>
                    </div>
                  </div>
                  <div className="mb-2">
                    <div className="border p-2 rounded">
                      <div>Total Shifts</div>
                      <div className="font-weight-bold">
                        {getAllShifts().length}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-4">
                <div className="bg-color-fcfcfc border h-100 p-3 rounded">
                  <div className="font-weight-bold">Shift:</div>
                  <div className="mb-3 mt-1">
                    Identifies your manfacturing unit. Your devices, data and
                    reports belong to a plant.
                  </div>
                  <div className="font-weight-bold">Instance:</div>
                  <div className="mb-3 mt-1">
                    Choose a name that helps you identify a plant, factory or a
                    warehouse.
                  </div>
                  <div className="font-weight-bold">Note:</div>
                  <div className="mb-3 mt-1">
                    The administrative can carry out all possible tasks for a
                    plant, including more users.
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex align-items-center justify-content-between">
          {currentScreen === 4 ? (
            <div className="tw-font-bold">
              <Form.Check
                type="checkbox"
                id="default_checkbox"
                label="I consent to creating a new plant"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
              />
            </div>
          ) : currentScreen > 1 ? (
            <div className="cursor-pointer" onClick={handleBackBtnClick}>
              Go Back
            </div>
          ) : (
            <div></div>
          )}
          <div>
            <Button variant="outline-danger" onClick={handleClose}>
              Discard
            </Button>
            {currentScreen < screens.length && (
              <Button className="ms-2" variant="primary" onClick={handleNext}>
                Save & Next
              </Button>
            )}
            {currentScreen === screens.length && (
              <Button
                className="ms-2"
                variant="primary"
                onClick={handleSubmit}
                disabled={loading || updateLoading || !consentChecked}
              >
                {loading || updateLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Saving...
                  </>
                ) : mode === "edit" ? (
                  "Update Plant"
                ) : (
                  "Add a Plant"
                )}
              </Button>
            )}
          </div>
        </Modal.Footer>
      </Modal>
      <style jsx>{`
        .alert-danger {
          background-color: #fff5f5;
          border-color: #feb2b2;
          color: #c53030;
        }

        .alert-danger hr {
          border-top-color: #feb2b2;
        }

        .alert-heading {
          color: #c53030;
          margin-bottom: 0.5rem;
        }

        .alert-danger ul {
          padding-left: 1.5rem;
        }

        .alert-danger li {
          margin-bottom: 0.25rem;
        }
      `}</style>
    </>
  );
}

export default AddPlantModal;
